/**
 * GlobalPazienteSearchHost — campo header "Cerca paziente".
 *
 * Leggero: import statico da AppTopBar (main chunk).
 * Lo stato del testo vive qui. Il panel (query + dropdown) arriva
 * con import() al primo focus o alla prima digitazione.
 *
 * Nessun import statico di GlobalPazienteSearchPanel: il grafo di
 * main.tsx non deve raggiungere pazienti-search.
 */

import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type RefObject,
} from 'react';
import { clsx } from 'clsx';
import { Icon } from '@shared/components/ui/Icon';

interface ComboboxA11y {
  expanded: boolean;
  activeId: string | undefined;
}

interface PanelProps {
  draft: string;
  onDraftChange: (value: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  rootRef: RefObject<HTMLElement | null>;
  listId: string;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onA11yChange: (state: ComboboxA11y) => void;
}

type PanelComponent = (props: PanelProps) => ReactElement | null;

let panelLoader: Promise<{ GlobalPazienteSearchPanel: PanelComponent }> | null =
  null;

function loadPanel() {
  if (!panelLoader) {
    panelLoader = import('./GlobalPazienteSearchPanel');
  }
  return panelLoader;
}

export function GlobalPazienteSearchHost() {
  const inputId = useId();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const [draft, setDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [Panel, setPanel] = useState<PanelComponent | null>(null);
  const [loadingModule, setLoadingModule] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [a11y, setA11y] = useState<ComboboxA11y>({
    expanded: false,
    activeId: undefined,
  });

  const ensurePanel = useCallback(() => {
    if (Panel || loadingModule) return;
    setLoadingModule(true);
    setLoadError(false);
    void loadPanel()
      .then((mod) => {
        setPanel(() => mod.GlobalPazienteSearchPanel);
      })
      .catch(() => {
        panelLoader = null;
        setLoadError(true);
      })
      .finally(() => {
        setLoadingModule(false);
      });
  }, [Panel, loadingModule]);

  function handleFocus() {
    ensurePanel();
    if (draft.trim().length >= 2) setMenuOpen(true);
  }

  function handleChange(value: string) {
    setDraft(value);
    setMenuOpen(true);
    ensurePanel();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape' && !Panel) {
      setMenuOpen(false);
      setDraft('');
      setMobileOpen(false);
    }
  }

  function openMobile() {
    setMobileOpen(true);
    ensurePanel();
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }

  const showModuleLoading =
    loadingModule && !Panel && menuOpen && draft.trim().length >= 2;

  return (
    <div
      ref={rootRef}
      data-testid="global-paziente-search"
      className={clsx(
        'flex items-center min-w-0',
        mobileOpen ? 'flex-1' : 'ml-auto md:ml-0 md:flex-1 md:max-w-md'
      )}
    >
      <button
        type="button"
        className={clsx(
          'md:hidden w-11 h-11 rounded-full flex items-center justify-center text-text-secondary',
          'hover:bg-bg-app focus:outline-none focus-visible:ring-2 focus-visible:ring-magenta-soft',
          mobileOpen && 'hidden'
        )}
        aria-label="Cerca paziente"
        onClick={openMobile}
      >
        <Icon name="search" size={22} />
      </button>

      <div
        className={clsx(
          'relative w-full',
          mobileOpen ? 'block' : 'hidden md:block'
        )}
      >
        <label className="relative w-full" htmlFor={inputId}>
          <span className="sr-only">Cerca paziente</span>
          <Icon
            name="search"
            size={20}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none"
          />
          <input
            ref={inputRef}
            id={inputId}
            type="search"
            role="combobox"
            aria-label="Cerca paziente"
            aria-expanded={a11y.expanded}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={a11y.activeId}
            placeholder="Cerca paziente"
            autoComplete="off"
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            className="w-full h-11 pl-10 pr-3 rounded-lg border border-border-gray bg-bg-app font-body-md text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus-visible:ring-magenta-soft"
          />
        </label>

        {showModuleLoading && (
          <ul
            className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-border-gray rounded-lg shadow-lg"
            role="listbox"
            id={listId}
          >
            <li className="px-3 py-2 font-body-sm text-text-secondary">
              Caricamento…
            </li>
          </ul>
        )}

        {loadError && (
          <p
            role="alert"
            className="absolute z-40 top-full left-0 right-0 mt-1 px-3 py-2 bg-white border border-border-gray rounded-lg font-body-sm text-text-secondary"
          >
            Impossibile caricare la ricerca. Ricarica la pagina.
          </p>
        )}

        {Panel && (
          <Panel
            draft={draft}
            onDraftChange={setDraft}
            inputRef={inputRef}
            rootRef={rootRef}
            listId={listId}
            menuOpen={menuOpen}
            onMenuOpenChange={setMenuOpen}
            onA11yChange={setA11y}
          />
        )}
      </div>
    </div>
  );
}
